import { classifyBashCommand, stripShellWrapper } from "./classifyBashCommand"

const MAX_RICH_DIFF_LENGTH = 250_000
const MAX_GIT_ROWS = 200

export interface GitStatusEntry {
    status: string
    path: string
}

export interface GitLogEntry {
    hash: string
    subject: string
}

export type CommandOutputPresentation =
    | { kind: "git_diff"; patch: string }
    | { kind: "git_status"; branch?: string; entries: GitStatusEntry[] }
    | { kind: "git_log"; commits: GitLogEntry[] }
    | { kind: "verification"; label: string; summary: string; output: string; failure?: string }
    | { kind: "terminal" }

export function getCommandOutputPresentation(command: string, output: string | undefined): CommandOutputPresentation {
    const innerCommand = stripShellWrapper(command)
    const result = output ?? ""

    if (hasGitSubcommand(innerCommand, "diff") || hasGitSubcommand(innerCommand, "show")) {
        const diffStart = result.indexOf("diff --git ")
        if (diffStart >= 0) {
            const patch = result.slice(diffStart)
            if (patch.length <= MAX_RICH_DIFF_LENGTH) return { kind: "git_diff", patch }
        }
    }

    if (hasGitSubcommand(innerCommand, "status") && /(?:^|\s)(?:--short|-s\b|--porcelain\b|-[a-zA-Z]*s[a-zA-Z]*\b)/.test(innerCommand)) {
        const status = parseGitStatus(result)
        if (status) return { kind: "git_status", ...status }
    }

    if (hasGitSubcommand(innerCommand, "log")) {
        const commits = parseGitLog(result)
        if (commits) return { kind: "git_log", commits }
    }

    const classification = classifyBashCommand(command)
    if (classification.semanticType === "verify") {
        return {
            kind: "verification",
            label: verificationLabel(classification.label),
            summary: verificationSummary(result),
            output: result,
            ...verificationFailure(result),
        }
    }

    return { kind: "terminal" }
}

function verificationFailure(output: string): { failure?: string } {
    const line = output
        .split("\n")
        .map((candidate) => candidate.trim())
        .find((candidate) => /^(?:FAIL\b|--- FAIL:|Error:|error TS\d+|\[ERROR\])/.test(candidate))
    if (!line) return {}
    return { failure: line.length > 160 ? `${line.slice(0, 157)}…` : line }
}

function hasGitSubcommand(command: string, subcommand: string): boolean {
    return new RegExp(`(?:^|&&|\\|\\||;|\\n)\\s*git\\s+${subcommand}\\b`).test(command)
}

function parseGitStatus(output: string): { branch?: string; entries: GitStatusEntry[] } | null {
    const lines = output
        .split("\n")
        .map((line) => line.trimEnd())
        .filter(Boolean)
    if (lines.length > MAX_GIT_ROWS + 1) return null

    let branch: string | undefined
    const entries: GitStatusEntry[] = []
    for (const line of lines) {
        if (line.startsWith("## ")) {
            branch = line.slice(3)
            continue
        }
        const match = line.match(/^(.{2}) (.+)$/)
        if (!match?.[1] || !match[2]) return null
        entries.push({ status: match[1].trim() || match[1], path: match[2] })
    }
    return { ...(branch ? { branch } : {}), entries }
}

function parseGitLog(output: string): GitLogEntry[] | null {
    const lines = output
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    if (lines.length === 0 || lines.length > MAX_GIT_ROWS) return null

    const commits: GitLogEntry[] = []
    for (const line of lines) {
        const match = line.match(/^([0-9a-f]{7,40})\s+(.+)$/i)
        if (!match?.[1] || !match[2]) return null
        commits.push({ hash: match[1], subject: match[2] })
    }
    return commits
}

function verificationLabel(label: string): string {
    if (/test|vitest|pytest/i.test(label)) return "Tests"
    if (/typecheck|lint|biome|eslint|oxfmt/i.test(label)) return "Checks"
    return label
}

function verificationSummary(output: string): string {
    const lines = output.split("\n")
    const testsLine = lines.find((line) => /^\s*Tests\s*:?[ \t]/.test(line))
    const filesLine = lines.find((line) => /^\s*Test Files\s*:?[ \t]/.test(line))
    const parts: string[] = []
    const passed = testsLine?.match(/(\d+)\s+passed/i)?.[1]
    const failed = testsLine?.match(/(\d+)\s+failed/i)?.[1]
    const files = filesLine?.match(/(\d+)\s+passed/i)?.[1]
    if (passed) parts.push(`${passed} passed`)
    if (failed) parts.push(`${failed} failed`)
    if (files) parts.push(`${files} files`)
    if (parts.length > 0) return parts.join(" · ")

    const genericPassed = output.match(/\b(\d+)\s+tests?\s+passed\b/i)?.[1]
    if (genericPassed) return `${genericPassed} passed`
    const goPackages = lines.filter((line) => /^ok\s+\S+/.test(line)).length
    if (goPackages > 0) return `${goPackages} package${goPackages === 1 ? "" : "s"} passed`
    return "Completed"
}
